// The chart's right-rail panel (TODO §15/§7).
//
// **Three groups, and the split is the feature** (§7.3):
//
//   1. **This chart** — type, encoding, stacked/horizontal/donut, axis titles, number format,
//      data labels. Written to the chart DOC, so it changes one chart.
//   2. **Data source** — manual / imported / a URL with a refresh rule (§5). Also the doc.
//   3. **Deck chart style** — palette, axis, gridlines, legend, series geometry. Written to
//      the deck STYLESHEET, so it changes every chart in the deck, and the group says so in
//      as many words. A control whose blast radius is the whole deck sitting unlabelled next
//      to one that changes a single chart is how people lose an afternoon.
//
// §7.4 The deck-style edits go through `setStyleSheet` like any other stylesheet commit —
// **not** through Gridd's draft/commit protocol, which is the editor's own and has no way to
// carry a key it has never heard of. Going through the action is what puts them in front of
// the persistence listener and, on the next commit from Gridd's editor, in front of
// `mergeChartStyles` (§6.3.2).
//
// Lazily loaded through the same door as the block (§2.4): opening this panel in a deck with
// no chart must not pull the Recharts chunk. Nothing here imports it — the panel edits data,
// and the block does the drawing.

import { useMemo, useState } from "react";
import { Info } from "lucide-react";

import { useChartEditing } from "@/components/deck/chartEditing";
import { SwatchRow } from "@/components/deck/SwatchRow";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  CHART_MIN_INTERVAL_MINUTES,
  columnLabel,
  defaultEncoding,
  NUMBER_FORMAT_LABELS,
} from "@/lib/chart";
import {
  describeTransport,
  guessFormat,
  validateSourceUrl,
} from "@/lib/chartFetch";
import {
  currentChartPalette,
  currentChartStyles,
  resolveChartStyle,
  writeChartStyles,
} from "@/lib/stylesheet-chart";
import { sheetSwatches, swatchValue } from "@/lib/stylesheet-typography";
import { chartDocUpdated } from "@/store/chartSlice";
import { setStyleSheet } from "@/store/documentSlice";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { selectEffectiveStyleSheet } from "@/store/styleDraftSlice";
import type {
  ChartDocument,
  ChartLegendPosition,
  ChartStyleBlock,
  ChartType,
} from "@/types/chart";

const TYPES: { value: ChartType; label: string }[] = [
  { value: "bar", label: "Bar" },
  { value: "line", label: "Line" },
  { value: "area", label: "Area" },
  { value: "pie", label: "Pie" },
  { value: "scatter", label: "Scatter" },
  { value: "combo", label: "Combo" },
];

const LEGEND_POSITIONS: ChartLegendPosition[] = [
  "none",
  "top",
  "bottom",
  "left",
  "right",
];

export function ChartPanel() {
  const dispatch = useAppDispatch();
  const { activeDocId } = useChartEditing();
  const doc = useAppSelector((s) =>
    activeDocId ? s.chart[activeDocId] : undefined,
  );

  // The EFFECTIVE sheet for reading (so the panel's swatches match what a live stylesheet
  // preview is currently painting), the COMMITTED one for writing. Writing the effective one
  // would promote an uncommitted Gridd draft into the document as a side effect of picking a
  // chart colour — a Cancel in the stylesheet editor would then not cancel.
  //
  // The two cannot actually be in play at once, which is what keeps this from being a real
  // split-brain: only one right panel is open at a time, and reaching this one from the
  // stylesheet editor goes through `switchPanel` → `leavingStylePanelIsOk`, which abandons the
  // draft (after confirming) before the switch. So by the time this panel renders,
  // `effectiveStyleSheet === committedStyleSheet`. Reading the effective one anyway costs
  // nothing and is correct if that ever stops being true.
  const effectiveStyleSheet = useAppSelector(selectEffectiveStyleSheet);
  const committedStyleSheet = useAppSelector((s) => s.document.styleSheet);
  const swatches = useMemo(
    () => sheetSwatches(effectiveStyleSheet),
    [effectiveStyleSheet],
  );
  const resolved = useMemo(
    () => resolveChartStyle(effectiveStyleSheet),
    [effectiveStyleSheet],
  );
  const block = useMemo(
    () => currentChartStyles(committedStyleSheet),
    [committedStyleSheet],
  );
  const palette = useMemo(
    () => currentChartPalette(committedStyleSheet),
    [committedStyleSheet],
  );

  const patchDoc = (patch: Partial<ChartDocument>) => {
    if (!doc || !activeDocId) return;
    dispatch(
      chartDocUpdated({ docId: activeDocId, doc: { ...doc, ...patch } }),
    );
  };
  const patchOptions = (patch: Partial<ChartDocument["options"]>) =>
    doc && patchDoc({ options: { ...doc.options, ...patch } });

  // §7.4 Every deck-style write goes through here, so there is exactly one place that knows
  // the panel edits the committed sheet directly.
  const patchStyle = (patch: Partial<ChartStyleBlock>) => {
    dispatch(
      setStyleSheet(
        writeChartStyles(committedStyleSheet, {
          chartStyles: { ...block, ...patch, version: 1 },
        }),
      ),
    );
  };
  const patchPalette = (categorical: string[]) => {
    dispatch(
      setStyleSheet(writeChartStyles(committedStyleSheet, { categorical })),
    );
  };

  return (
    <div className="h-full space-y-5 overflow-y-auto p-3">
      {/* §7.2 The panel is about the SELECTION. It is always offered from the rail — hiding it
          when nothing is selected would make it a control that appears and disappears — so it
          explains itself instead, the same shape the diagram and gantt panels use. */}
      {!doc ? (
        <p className="text-xs text-slate-500">
          Select a chart on a slide to edit it. The deck-wide chart style below
          is still editable.
        </p>
      ) : (
        <>
          <Group title="This chart" note="Affects only the selected chart.">
            <Field label="Type">
              <Select
                value={doc.type}
                onChange={(value) => patchDoc({ type: value as ChartType })}
                options={TYPES.map((t) => [t.value, t.label])}
              />
            </Field>

            <Field label="Category axis (X)">
              <Select
                value={doc.encoding.x}
                onChange={(x) => patchDoc({ encoding: { ...doc.encoding, x } })}
                options={doc.columns.map((c) => [c.id, c.label])}
              />
            </Field>

            <SeriesEditor
              doc={doc}
              swatches={swatches}
              defaultColorFor={(i) =>
                resolved.palette[i % resolved.palette.length]
              }
              onChange={(encoding) => patchDoc({ encoding })}
            />

            {/* Only the switches this chart type can act on. A "Donut" toggle on a bar chart
                is a control that does nothing, and a panel full of those teaches people not
                to trust any of it. */}
            {(doc.type === "bar" ||
              doc.type === "area" ||
              doc.type === "combo") && (
              <Toggle
                label="Stack the series"
                checked={doc.options.stacked ?? false}
                onChange={(stacked) => patchOptions({ stacked })}
              />
            )}
            {doc.type === "bar" && (
              <Toggle
                label="Horizontal bars"
                checked={doc.options.horizontal ?? false}
                onChange={(horizontal) => patchOptions({ horizontal })}
              />
            )}
            {doc.type === "pie" && (
              <Toggle
                label="Donut"
                checked={doc.options.donut ?? false}
                onChange={(donut) => patchOptions({ donut })}
              />
            )}
            {/* A scatter's X is a MEASUREMENT, not a category — it goes on a numeric axis
                (`chartData`), so a text column there plots nothing at all. Said here rather
                than silently repaired, because "which column is the X" is the author's
                decision and quietly changing it is worse than an empty chart with a reason. */}
            {doc.type === "scatter" &&
              doc.columns.find((c) => c.id === doc.encoding.x)?.type !==
                "number" && (
                <p className="text-[11px] text-amber-600">
                  A scatter plots numbers against numbers — pick a numeric
                  column for the X axis, or change that column's type in the
                  chart's Data view.
                </p>
              )}
            {doc.type === "pie" && doc.encoding.series.length > 1 && (
              <p className="text-[11px] text-slate-500">
                A pie shows one series — it is drawing{" "}
                <strong>
                  {columnLabel(doc, doc.encoding.series[0].columnId)}
                </strong>
                .
              </p>
            )}

            <Field label="Axis titles">
              <div className="flex gap-1.5">
                <Input
                  className="h-7 text-xs"
                  placeholder="X"
                  value={doc.options.xTitle ?? ""}
                  onChange={(e) => patchOptions({ xTitle: e.target.value })}
                />
                <Input
                  className="h-7 text-xs"
                  placeholder="Y"
                  value={doc.options.yTitle ?? ""}
                  onChange={(e) => patchOptions({ yTitle: e.target.value })}
                />
              </div>
            </Field>

            <Field label="Number format">
              <Select
                value={doc.options.numberFormat ?? "auto"}
                onChange={(numberFormat) => patchOptions({ numberFormat })}
                options={Object.entries(NUMBER_FORMAT_LABELS)}
              />
            </Field>

            <Toggle
              label="Show data labels"
              checked={doc.options.showDataLabels ?? resolved.dataLabels.show}
              onChange={(showDataLabels) => patchOptions({ showDataLabels })}
            />
          </Group>

          <SourceEditor doc={doc} onChange={patchDoc} />
        </>
      )}

      {/* §7.3 The deck-wide group, labelled as such. Editable with no chart selected, because
          it is a property of the DECK and there is nothing about it that needs a selection. */}
      <Group
        title="Deck chart style"
        note="Affects every chart in this deck, and travels with it in the stylesheet."
      >
        <Field label="Palette">
          <PaletteEditor
            palette={palette}
            swatches={swatches}
            resolved={resolved.palette}
            onChange={patchPalette}
          />
        </Field>

        <Field label="Axis colour">
          <SwatchRow
            swatches={swatches}
            value={block.axis?.colorName}
            onPick={(colorName) =>
              patchStyle({ axis: { ...block.axis, colorName } })
            }
            inheritLabel="From text"
            inheritColor={resolved.axis.color}
          />
        </Field>

        <Field label="Axis text size">
          <NumberInput
            value={block.axis?.fontSizePt ?? resolved.axis.font.fontSizePt}
            min={4}
            max={48}
            suffix="pt"
            onChange={(fontSizePt) =>
              patchStyle({ axis: { ...block.axis, fontSizePt } })
            }
          />
        </Field>

        <Toggle
          label="Value gridlines"
          checked={block.gridlines?.y ?? true}
          onChange={(y) => patchStyle({ gridlines: { ...block.gridlines, y } })}
        />
        <Toggle
          label="Category gridlines"
          checked={block.gridlines?.x ?? false}
          onChange={(x) => patchStyle({ gridlines: { ...block.gridlines, x } })}
        />
        <Field label="Gridline colour">
          <SwatchRow
            swatches={swatches}
            value={block.gridlines?.colorName}
            onPick={(colorName) =>
              patchStyle({ gridlines: { ...block.gridlines, colorName } })
            }
            inheritLabel="From text"
            inheritColor={resolved.gridlines.color}
          />
        </Field>
        <Field label="Gridline style">
          <Select
            value={block.gridlines?.dash ?? "3 3"}
            onChange={(dash) =>
              patchStyle({ gridlines: { ...block.gridlines, dash } })
            }
            options={[
              ["", "Solid"],
              ["3 3", "Dotted"],
              ["8 4", "Dashed"],
            ]}
          />
        </Field>

        <Field label="Legend">
          <Select
            value={block.legend?.position ?? "bottom"}
            onChange={(position) =>
              patchStyle({
                legend: {
                  ...block.legend,
                  position: position as ChartLegendPosition,
                },
              })
            }
            options={LEGEND_POSITIONS.map((p) => [
              p,
              p === "none" ? "Hidden" : p[0].toUpperCase() + p.slice(1),
            ])}
          />
        </Field>

        <Field label="Bar corner radius">
          <NumberInput
            value={block.series?.barRadius ?? resolved.series.barRadius}
            min={0}
            max={24}
            onChange={(barRadius) =>
              patchStyle({ series: { ...block.series, barRadius } })
            }
          />
        </Field>
        <Field label="Bar gap">
          <NumberInput
            value={block.series?.barGapPct ?? resolved.series.barGapPct}
            min={0}
            max={80}
            suffix="%"
            onChange={(barGapPct) =>
              patchStyle({ series: { ...block.series, barGapPct } })
            }
          />
        </Field>
        <Field label="Line width">
          <NumberInput
            value={block.series?.lineWidth ?? resolved.series.lineWidth}
            min={1}
            max={12}
            onChange={(lineWidth) =>
              patchStyle({ series: { ...block.series, lineWidth } })
            }
          />
        </Field>
        <Field label="Line shape">
          <Select
            value={block.series?.curve ?? resolved.series.curve}
            onChange={(curve) =>
              patchStyle({
                series: {
                  ...block.series,
                  curve: curve as NonNullable<
                    ChartStyleBlock["series"]
                  >["curve"],
                },
              })
            }
            options={[
              ["monotone", "Smooth"],
              ["linear", "Straight"],
              ["step", "Stepped"],
            ]}
          />
        </Field>
        <Field label="Point size">
          <NumberInput
            value={block.series?.pointSize ?? resolved.series.pointSize}
            min={0}
            max={12}
            onChange={(pointSize) =>
              patchStyle({ series: { ...block.series, pointSize } })
            }
          />
        </Field>
        <Field label="Area fill">
          <NumberInput
            value={Math.round(
              (block.series?.areaOpacity ?? resolved.series.areaOpacity) * 100,
            )}
            min={0}
            max={100}
            suffix="%"
            onChange={(pct) =>
              patchStyle({
                series: { ...block.series, areaOpacity: pct / 100 },
              })
            }
          />
        </Field>

        {/* §11.3 Not decoration: colour alone fails a projector, a monochrome print and a
            reader who cannot separate two hues, and this is the setting that fixes all three
            at once. Off by default because a solid line is the better default when colour
            does work. */}
        <Toggle
          label="Vary line dashes by series"
          hint="So series stay distinguishable without relying on colour."
          checked={block.series?.varyDashes ?? false}
          onChange={(varyDashes) =>
            patchStyle({ series: { ...block.series, varyDashes } })
          }
        />

        <Field label="Plot background">
          <SwatchRow
            swatches={swatches}
            value={block.plot?.backgroundColorName}
            onPick={(backgroundColorName) =>
              patchStyle({ plot: { ...block.plot, backgroundColorName } })
            }
            inheritLabel="None"
            inheritColor="transparent"
          />
        </Field>
      </Group>
    </div>
  );
}

// === The series list =======================================================================
//
// Which columns are plotted, in what order, in what colour, on which axis — and for a combo,
// as what mark. A checkbox per column rather than an add/remove list: "which of my columns are
// on the chart" is the question being answered, and a list of the ones that are makes the
// answer require counting.
function SeriesEditor({
  doc,
  swatches,
  defaultColorFor,
  onChange,
}: {
  doc: ChartDocument;
  swatches: { name: string; value: string }[];
  defaultColorFor: (index: number) => string;
  onChange: (encoding: ChartDocument["encoding"]) => void;
}) {
  const plotted = doc.columns.filter((c) => c.id !== doc.encoding.x);
  const indexOf = (columnId: string) =>
    doc.encoding.series.findIndex((s) => s.columnId === columnId);

  const toggle = (columnId: string, on: boolean) => {
    onChange({
      ...doc.encoding,
      series: on
        ? [...doc.encoding.series, { columnId }]
        : doc.encoding.series.filter((s) => s.columnId !== columnId),
    });
  };
  const patchSeries = (
    columnId: string,
    patch: Partial<ChartDocument["encoding"]["series"][number]>,
  ) => {
    onChange({
      ...doc.encoding,
      series: doc.encoding.series.map((s) =>
        s.columnId === columnId ? { ...s, ...patch } : s,
      ),
    });
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label className="text-xs text-slate-500">Series</Label>
        <Button
          variant="ghost"
          size="sm"
          className="h-6 px-2 text-[11px]"
          onClick={() => onChange(defaultEncoding(doc.columns))}
        >
          Reset
        </Button>
      </div>
      {plotted.length === 0 && (
        <p className="text-[11px] text-slate-500">
          Every column is the category axis — add a column in the chart's Data
          view to plot something.
        </p>
      )}
      {plotted.map((column) => {
        const index = indexOf(column.id);
        const series = index >= 0 ? doc.encoding.series[index] : null;
        return (
          <div
            key={column.id}
            className="space-y-1.5 rounded border border-slate-200 p-2"
          >
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={Boolean(series)}
                onChange={(e) => toggle(column.id, e.target.checked)}
                aria-label={`Plot ${column.label}`}
              />
              <span className="min-w-0 flex-1 truncate text-xs">
                {column.label}
              </span>
              {column.type !== "number" && (
                <span
                  className="shrink-0 text-[10px] text-amber-600"
                  title="Only numeric columns plot. Change the column's type in the Data view."
                >
                  not numeric
                </span>
              )}
            </div>
            {series && (
              <>
                <SwatchRow
                  swatches={swatches}
                  value={series.colorName}
                  onPick={(colorName) => patchSeries(column.id, { colorName })}
                  inheritLabel="Palette"
                  inheritColor={defaultColorFor(index)}
                />
                <div className="flex gap-1.5">
                  {doc.type === "combo" && (
                    <Select
                      value={series.type ?? "bar"}
                      onChange={(value) =>
                        patchSeries(column.id, {
                          type: value as "bar" | "line" | "area",
                        })
                      }
                      options={[
                        ["bar", "Bar"],
                        ["line", "Line"],
                        ["area", "Area"],
                      ]}
                    />
                  )}
                  <Select
                    value={series.yAxis ?? "left"}
                    onChange={(value) =>
                      patchSeries(column.id, {
                        yAxis: value as "left" | "right",
                      })
                    }
                    options={[
                      ["left", "Left axis"],
                      ["right", "Right axis"],
                    ]}
                  />
                </div>
              </>
            )}
          </div>
        );
      })}
    </div>
  );
}

// === §5 The data source ====================================================================
function SourceEditor({
  doc,
  onChange,
}: {
  doc: ChartDocument;
  onChange: (patch: Partial<ChartDocument>) => void;
}) {
  // Local, so a half-typed URL is not written to the doc on every keystroke — which would
  // also mean a half-typed URL being fetched by the interval timer.
  const [draftUrl, setDraftUrl] = useState(
    doc.source.kind === "url" ? doc.source.url : "",
  );
  const source = doc.source;
  const urlSource = source.kind === "url" ? source : null;
  const urlError = draftUrl ? validateSourceUrl(draftUrl) : null;

  return (
    <Group
      title="Data source"
      note={
        source.kind === "csv"
          ? `Imported from ${source.fileName}.`
          : source.kind === "manual"
            ? "Typed in the chart's Data view."
            : undefined
      }
    >
      {!urlSource ? (
        <Button
          variant="secondary"
          size="sm"
          className="h-7 w-full text-xs"
          onClick={() =>
            onChange({
              source: {
                kind: "url",
                url: "",
                format: "csv",
                refresh: { mode: "manual" },
              },
            })
          }
        >
          Use a live URL
        </Button>
      ) : (
        <>
          {/* §5.2.5 The capability, stated where the URL is typed — never discovered later at
              the bottom of an error message. */}
          <p className="flex gap-1.5 rounded bg-slate-50 p-2 text-[11px] text-slate-600">
            <Info className="mt-0.5 h-3 w-3 shrink-0" />
            <span>{describeTransport()}</span>
          </p>

          <Field label="URL">
            <Input
              className="h-7 text-xs"
              placeholder="https://…"
              value={draftUrl}
              onChange={(e) => setDraftUrl(e.target.value)}
              onBlur={() =>
                onChange({
                  source: {
                    ...urlSource,
                    url: draftUrl,
                    format: guessFormat(draftUrl),
                  },
                })
              }
            />
          </Field>
          {urlError && <p className="text-[11px] text-red-600">{urlError}</p>}

          <Field label="Format">
            <Select
              value={urlSource.format}
              onChange={(format) =>
                onChange({
                  source: { ...urlSource, format: format as "csv" | "json" },
                })
              }
              options={[
                ["csv", "CSV"],
                ["json", "JSON"],
              ]}
            />
          </Field>
          {urlSource.format === "json" && (
            <Field label="Path to the rows">
              <Input
                className="h-7 text-xs"
                placeholder="data.rows"
                value={urlSource.jsonPath ?? ""}
                onChange={(e) =>
                  onChange({
                    source: { ...urlSource, jsonPath: e.target.value },
                  })
                }
              />
            </Field>
          )}

          <Field label="Refresh">
            <Select
              value={urlSource.refresh.mode}
              onChange={(mode) =>
                onChange({
                  source: {
                    ...urlSource,
                    refresh: {
                      ...urlSource.refresh,
                      mode: mode as "manual" | "on-open" | "interval",
                    },
                  },
                })
              }
              options={[
                ["manual", "Only when I ask"],
                ["on-open", "When the deck opens"],
                ["interval", "Every few minutes"],
              ]}
            />
          </Field>
          {urlSource.refresh.mode === "interval" && (
            <Field label="Every">
              <NumberInput
                value={
                  urlSource.refresh.intervalMinutes ??
                  CHART_MIN_INTERVAL_MINUTES
                }
                min={CHART_MIN_INTERVAL_MINUTES}
                max={720}
                suffix="min"
                onChange={(intervalMinutes) =>
                  onChange({
                    source: {
                      ...urlSource,
                      refresh: { ...urlSource.refresh, intervalMinutes },
                    },
                  })
                }
              />
            </Field>
          )}

          {/* §5.3.4 Said in the UI, not only in the code: an exported or presented deck shows
              the rows it has, and never goes to the network. That is a promise about how a
              deck behaves in a room with no wifi, and it should not be a surprise. */}
          <p className="text-[11px] text-slate-400">
            Presentations, PDFs and exports always use the last data that was
            fetched — they never go to the network.
          </p>

          <Button
            variant="ghost"
            size="sm"
            className="h-7 w-full text-xs"
            onClick={() =>
              onChange({
                source: { kind: "manual" },
              })
            }
          >
            Stop using a URL
          </Button>
        </>
      )}
    </Group>
  );
}

// === §7.5 The palette editor ===============================================================
//
// Slots over the sheet's own colours, and **no hex field anywhere** — that absence is what
// keeps §6.2.2 true in practice rather than only in the comments. An empty palette shows the
// derived default (`defaultPalette`), so what is on screen is always what the charts draw.
function PaletteEditor({
  palette,
  swatches,
  resolved,
  onChange,
}: {
  palette: string[];
  swatches: { name: string; value: string }[];
  resolved: string[];
  onChange: (names: string[]) => void;
}) {
  const slots = palette.length > 0 ? palette : [];
  return (
    <div className="space-y-1.5">
      {slots.length === 0 && (
        <p className="text-[11px] text-slate-500">
          Using this deck's own colours. Add a slot to choose the order.
        </p>
      )}
      {slots.map((name, i) => (
        <div key={i} className="flex items-center gap-1.5">
          <span className="w-4 shrink-0 text-[11px] text-slate-400">
            {i + 1}
          </span>
          <div className="min-w-0 flex-1">
            <SwatchRow
              swatches={swatches}
              value={name}
              onPick={(picked) =>
                onChange(
                  // Picking "inherit" on a slot REMOVES it — a palette slot with no colour is
                  // not a thing, and a hole in the cycle would draw an invisible series.
                  picked
                    ? slots.map((s, j) => (j === i ? picked : s))
                    : slots.filter((_, j) => j !== i),
                )
              }
              inheritLabel="Remove"
              inheritColor={swatchValue(swatches, name) ?? "transparent"}
            />
          </div>
        </div>
      ))}
      <Button
        variant="secondary"
        size="sm"
        className="h-6 px-2 text-[11px]"
        onClick={() =>
          onChange(
            [
              ...slots,
              // Seed the new slot from what that position already draws, so adding one changes
              // nothing until it is picked — an "Add" that silently recolours a chart is worse
              // than no button.
              swatches.find(
                (c) => c.value === resolved[slots.length % resolved.length],
              )?.name ??
                swatches[0]?.name ??
                "",
            ].filter(Boolean),
          )
        }
      >
        Add a colour
      </Button>
    </div>
  );
}

// === Small shared controls =================================================================

function Group({
  title,
  note,
  children,
}: {
  title: string;
  note?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="space-y-2.5">
      <div>
        <h3 className="text-xs font-semibold text-slate-700">{title}</h3>
        {note && <p className="text-[11px] text-slate-400">{note}</p>}
      </div>
      {children}
    </section>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1">
      <Label className="text-xs text-slate-500">{label}</Label>
      {children}
    </div>
  );
}

function Toggle({
  label,
  hint,
  checked,
  onChange,
}: {
  label: string;
  hint?: string;
  checked: boolean;
  onChange: (value: boolean) => void;
}) {
  return (
    <div className="flex items-start justify-between gap-2">
      <div className="min-w-0">
        <Label className="text-xs text-slate-600">{label}</Label>
        {hint && <p className="text-[11px] text-slate-400">{hint}</p>}
      </div>
      <Switch checked={checked} onCheckedChange={onChange} />
    </div>
  );
}

function Select({
  value,
  onChange,
  options,
}: {
  value: string;
  onChange: (value: string) => void;
  options: [string, string][];
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="h-7 w-full rounded border border-slate-200 bg-white px-1.5 text-xs"
    >
      {options.map(([v, label]) => (
        <option key={v} value={v}>
          {label}
        </option>
      ))}
    </select>
  );
}

function NumberInput({
  value,
  min,
  max,
  suffix,
  onChange,
}: {
  value: number;
  min: number;
  max: number;
  suffix?: string;
  onChange: (value: number) => void;
}) {
  return (
    <div className="flex items-center gap-1.5">
      <Input
        type="number"
        className="h-7 w-20 text-xs"
        value={value}
        min={min}
        max={max}
        onChange={(e) => {
          const next = Number(e.target.value);
          // Clamp on the way in rather than trusting the input's own min/max, which a browser
          // only enforces on the spinner and not on typed text.
          if (Number.isFinite(next)) {
            onChange(Math.min(max, Math.max(min, next)));
          }
        }}
      />
      {suffix && <span className="text-[11px] text-slate-400">{suffix}</span>}
    </div>
  );
}
